/**
 * §6.3 Voice engine & voice. Engine choice as Natural/Standard/Realtime cards;
 * all three voice groups always rendered, with the inactive engines' fields
 * disabled behind an explanatory tooltip (cross-field rule: disable, don't
 * hide). Switching engine resets the voice picker so a dead voice ID is never
 * silently carried across.
 */

import { Field } from "../Field";
import { Select } from "../Select";
import { Slider } from "../Slider";
import { cx } from "../../lib/format";
import type {
  AgentConfig,
  GeminiModel,
  RealtimeModel,
  RealtimeTurnDetection,
  TTSProvider,
  VoiceEngine,
} from "../../lib/types";
import { defaultVoiceId } from "./constants";
import { EngineCards } from "./EngineCards";
import { Tooltip } from "./Tooltip";
import type { DraftErrors } from "./validation";
import { CascadedVoicePicker, GeminiVoicePicker, RealtimeVoicePicker } from "./VoicePicker";

const REALTIME_MODEL_OPTIONS: Array<{ value: RealtimeModel; label: string }> = [
  { value: "gpt-realtime-2.1-mini", label: "Realtime mini — recommended, much cheaper per minute" },
  { value: "gpt-realtime-2.1", label: "Realtime flagship — best quality, materially pricier" },
];

// Sep 2025 is the default despite being older: measured voice-to-voice is
// roughly half the Dec snapshot's (820/712ms vs 1573/1587ms).
const GEMINI_MODEL_OPTIONS: Array<{ value: GeminiModel; label: string }> = [
  {
    value: "models/gemini-3.1-flash-live-preview",
    label: "Gemini 3.1 Flash Live (preview) — fastest, recommended",
  },
  {
    value: "models/gemini-2.5-flash-native-audio-preview-09-2025",
    label: "Gemini 2.5 native audio (Sep 2025) — stable fallback, ~3x slower",
  },
  {
    value: "models/gemini-2.5-flash-native-audio-preview-12-2025",
    label: "Gemini 2.5 native audio (Dec 2025) — newer, ~2x slower to respond",
  },
];

const TURN_DETECTION_OPTIONS: Array<{ value: RealtimeTurnDetection; label: string }> = [
  { value: "semantic_vad", label: "Semantic — patient with mid-thought pauses (recommended)" },
  { value: "server_vad", label: "Silence-based — replies after a fixed pause" },
];

export interface VoiceSectionProps {
  config: AgentConfig;
  patch: (partial: Partial<AgentConfig>) => void;
  errors: DraftErrors;
  /** SUM(token_count) of ready docs — drives the §9.4 realtime warning. */
  readyKnowledgeTokens: number;
}

export function VoiceSection({ config, patch, errors, readyKnowledgeTokens }: VoiceSectionProps) {
  const cascaded = config.voice_engine === "cascaded";
  const realtime = config.voice_engine === "realtime";
  const gemini = config.voice_engine === "gemini";
  const cascadedTooltip = "These apply to the Standard engine — switch above to edit them.";
  const realtimeTooltip = "These apply to the Realtime engine — switch above to edit them.";
  const geminiTooltip = "These apply to the Natural (Google) engine — switch above to edit them.";

  /** §6.3: switching engines invalidates the voice — reset the picker. */
  function changeEngine(engine: VoiceEngine) {
    if (engine === config.voice_engine) return;
    if (engine === "cascaded") {
      patch({ voice_engine: engine, voice_id: defaultVoiceId(config.tts_provider) });
    } else if (engine === "gemini") {
      patch({ voice_engine: engine, gemini_voice: "Charon" });
    } else {
      patch({ voice_engine: engine, realtime_voice: "marin" });
    }
  }

  /** Voice IDs are provider-specific — changing provider resets the voice too. */
  function changeProvider(provider: TTSProvider) {
    if (provider === config.tts_provider) return;
    patch({ tts_provider: provider, voice_id: defaultVoiceId(provider) });
  }

  return (
    <div className="flex flex-col gap-5">
      <EngineCards
        value={config.voice_engine}
        onChange={changeEngine}
        readyKnowledgeTokens={readyKnowledgeTokens}
      />

      {/* Natural (Gemini) voice group */}
      <Tooltip content={geminiTooltip} block disabled={gemini}>
        <fieldset
          className={cx(
            "flex flex-col gap-4 rounded border border-line p-4",
            !gemini && "opacity-60",
          )}
        >
          <legend className="px-1 text-xs font-medium uppercase tracking-wide text-muted">
            Natural voice
          </legend>

          <Field
            label="Gemini model"
            help="On this engine the model is both the brain and the voice."
          >
            <Select
              value={config.gemini_model}
              disabled={!gemini}
              options={GEMINI_MODEL_OPTIONS}
              onChange={(event) => patch({ gemini_model: event.target.value as GeminiModel })}
            />
          </Field>

          <GeminiVoicePicker
            value={config.gemini_voice}
            onChange={(gemini_voice) => patch({ gemini_voice })}
            error={gemini ? errors.gemini_voice : null}
            disabled={!gemini}
          />

          <p className="text-xs text-muted">
            Gemini detects turns itself; how long it waits is the Response speed setting
            under Conversation.
          </p>
        </fieldset>
      </Tooltip>

      {/* Standard (cascaded) voice group */}
      <Tooltip content={cascadedTooltip} block disabled={cascaded}>
        <fieldset
          className={cx(
            "flex flex-col gap-4 rounded border border-line p-4",
            !cascaded && "opacity-60",
          )}
        >
          <legend className="px-1 text-xs font-medium uppercase tracking-wide text-muted">
            Standard voice
          </legend>

          <Field label="Voice provider" help="Where the voice itself comes from.">
            <Select
              value={config.tts_provider}
              disabled={!cascaded}
              options={[
                { value: "cartesia", label: "Cartesia — largest voice library" },
                { value: "deepgram", label: "Deepgram — fast, solid voices" },
              ]}
              onChange={(event) => changeProvider(event.target.value as TTSProvider)}
            />
          </Field>

          <CascadedVoicePicker
            provider={config.tts_provider}
            voiceId={config.voice_id}
            onChange={(voice_id) => patch({ voice_id })}
            error={cascaded ? errors.voice_id : null}
            disabled={!cascaded}
          />

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-body">Speaking speed</span>
            <Slider
              value={config.speed}
              onChange={(v) => patch({ speed: Number(v.toFixed(2)) })}
              min={0.7}
              max={1.3}
              step={0.05}
              disabled={!cascaded}
              label="Speaking speed"
              format={(v) => `${v.toFixed(2)}×`}
            />
            {errors.speed && cascaded && (
              <p className="text-xs text-danger" role="alert">
                {errors.speed}
              </p>
            )}
          </div>
        </fieldset>
      </Tooltip>

      {/* Realtime voice group */}
      <Tooltip content={realtimeTooltip} block disabled={realtime}>
        <fieldset
          className={cx(
            "flex flex-col gap-4 rounded border border-line p-4",
            !realtime && "opacity-60",
          )}
        >
          <legend className="px-1 text-xs font-medium uppercase tracking-wide text-muted">
            Realtime voice
          </legend>

          <Field
            label="Realtime model"
            help="On Realtime this model is both the brain and the voice."
          >
            <Select
              value={config.realtime_model}
              disabled={!realtime}
              options={REALTIME_MODEL_OPTIONS}
              onChange={(event) => patch({ realtime_model: event.target.value as RealtimeModel })}
            />
          </Field>

          <RealtimeVoicePicker
            value={config.realtime_voice}
            onChange={(realtime_voice) => patch({ realtime_voice })}
            error={realtime ? errors.realtime_voice : null}
            disabled={!realtime}
          />

          <Field
            label="Turn detection"
            help="How the agent decides you've finished speaking."
          >
            <Select
              value={config.realtime_turn_detection}
              disabled={!realtime}
              options={TURN_DETECTION_OPTIONS}
              onChange={(event) =>
                patch({ realtime_turn_detection: event.target.value as RealtimeTurnDetection })
              }
            />
          </Field>
        </fieldset>
      </Tooltip>
    </div>
  );
}
